// src/components/course/AIChat.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BASELINE } from '../../../../util';

const AIChat = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const chatContainerRef = useRef(null);

  // Split answer into text and LaTeX segments
  const splitAnswerIntoSegments = (answer) => {
    const segments = [];
    // (1) $$...$$ first capture
    // (2) [whiteboard] ... [/whiteboard] capture
    const regex = /(\$\$[\s\S]+?\$\$)|(\[whiteboard\][\s\S]+?\[\/whiteboard\])/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(answer)) !== null) {
      if (match.index > lastIndex) {
        const textPart = answer.substring(lastIndex, match.index).trim();
        if (textPart) {
          segments.push({ text: textPart, latex: '' });
        }
      }
      // match[0] is either $$...$$ or [whiteboard]...[/whiteboard]
      const specialPart = match[0];

      // Add to segments array in the same format whether it's whiteboard or $$ block
      segments.push({ text: '', latex: specialPart });
      lastIndex = regex.lastIndex;
    }

    // Handle remaining text
    const remainingText = answer.substring(lastIndex).trim();
    if (remainingText) {
      segments.push({ text: remainingText, latex: '' });
    }

    // If no captures were made, return the whole text
    if (segments.length === 0) {
      segments.push({ text: answer, latex: '' });
    }

    return segments;
  };

  // Handle sending a chat message
  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;
    
    const newUserMessage = { role: 'user', content: chatInput.trim() };
    // Add user message to chat history
    setChatHistory(prev => [...prev, newUserMessage]);

    // SAT tutoring system message
    const systemMessage = {
      role: 'system',
      content: "You are SAT Tutoring, a friendly assistant specialized in SAT. Only answer questions related to SAT. Please be kind and helpful. When providing explanations, separate general explanation and LaTeX content by using [whiteboard] tags or $$ delimiters."
    };

    // Get the latest 10 messages (including user and AI dialog) with system message
    const latestMessages = [...chatHistory, newUserMessage].slice(-10);
    const messagesForPayload = [systemMessage, ...latestMessages];

    setChatInput('');

    try {
      const payload = { messages: messagesForPayload };
      const res = await axios.post(`${BASELINE}askai4`, payload);

      let segments;
      if (res.data && res.data.segments) {
        // Backend already provided segments array
        segments = res.data.segments;
      } else if (res.data && res.data.message) {
        // Backend provided unseparated response, parse it on frontend
        segments = splitAnswerIntoSegments(res.data.message);
      } else {
        console.error("Unexpected response structure", res.data);
        return;
      }

      // Additional separation: if a segment's text contains $$ syntax, further separate it
      const finalSegments = [];
      segments.forEach(segment => {
        if (segment.text && segment.text.includes('$$')) {
          // Apply splitAnswerIntoSegments again to text with $$ content
          const subSegments = splitAnswerIntoSegments(segment.text);
          // Each subSegment will have LaTeX separated if present
          finalSegments.push(...subSegments);
        } else {
          finalSegments.push(segment);
        }
      });
      segments = finalSegments;

      // Add new AI message with segments array to chat history
      const newAIMessage = { role: 'ai', segments };
      setChatHistory(prev => [...prev, newAIMessage]);
    } catch (error) {
      console.error("Error during API call:", error);
      // Optionally add error message to chat
      setChatHistory(prev => [...prev, { 
        role: 'ai', 
        segments: [{ text: "Sorry, I encountered an error. Please try again.", latex: '' }] 
      }]);
    }
  };

  // Handle KaTeX rendering in chat area
  useEffect(() => {
    if (chatContainerRef.current) {
      // Scroll to bottom of chat
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;

      // Render KaTeX math expressions
      if (window.renderMathInElement) {
        window.renderMathInElement(chatContainerRef.current, {
          delimiters: [
            { left: "$$", right: "$$", display: false },
            { left: "$", right: "$", display: false },
            { left: "\\(", right: "\\)", display: false },
            { left: "\\[", right: "\\]", display: true },
            { left: "\\begin{array}", right: "\\end{array}", display: true },
          ],
          throwOnError: false,
        });
      } else {
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js";
        script.integrity = "sha384-+VBxd3r6XgURycqtZ117nYw44OOcIax56Z4dCRWbxyPt0Koah1uHoK0o4+/RRE05";
        script.crossOrigin = "anonymous";
        script.defer = true;
        script.onload = () => {
          if (window.renderMathInElement && chatContainerRef.current) {
            window.renderMathInElement(chatContainerRef.current, {
              delimiters: [
                { left: "$$", right: "$$", display: false },
                { left: "$", right: "$", display: false },
                { left: "\\(", right: "\\)", display: false },
                { left: "\\[", right: "\\]", display: true },
                { left: "\\begin{array}", right: "\\end{array}", display: true },
              ],
              throwOnError: false,
            });
          }
        };
        document.body.appendChild(script);
      }
    }
  }, [chatHistory]);

  return (
    <div className="border-t border-slate-200 bg-white flex flex-col shrink-0">
      {/* Chat header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
            </svg>
          </div>
          <span className="text-sm font-bold text-slate-700">AI Tutor</span>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-blue-600 bg-blue-100 hover:bg-blue-200 transition-colors"
        >
          {isExpanded ? "▾ Collapse" : "▴ Expand"}
        </button>
      </div>

      {/* Chat messages */}
      <div
        ref={chatContainerRef}
        className={`${isExpanded ? 'h-[calc(60vh-60px)]' : 'h-[calc(20vh-60px)]'} overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-blue-200`}
      >
        {chatHistory.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-4">
            <p className="text-sm text-slate-400 font-medium">Ask the AI tutor anything about this section.</p>
          </div>
        )}
        {chatHistory.map((msg, index) => (
          <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-br-sm shadow-sm shadow-blue-200'
                : 'bg-slate-100 text-slate-800 rounded-bl-sm border border-slate-200'
            }`}>
              {msg.role === 'user' && (
                <div dangerouslySetInnerHTML={{ __html: msg.content }} />
              )}
              {msg.role === 'ai' && msg.segments ? (
                msg.segments.map((segment, idx) => (
                  <div key={idx}>
                    {segment.text && <div className="mb-1">{segment.text}</div>}
                    {segment.latex && (
                      <div className="my-1.5 bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700 overflow-x-auto">
                        {segment.latex}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                msg.role === 'ai' && <div dangerouslySetInnerHTML={{ __html: msg.content }} />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Input area */}
      <div className="border-t border-slate-100 p-3 flex gap-2 bg-slate-50">
        <input
          type="text"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.keyCode === 13) { e.preventDefault(); handleSendMessage(); }
          }}
          placeholder="Ask anything about this section…"
          className="flex-1 bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition-all chat-input"
        />
        <button
          onClick={handleSendMessage}
          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl text-sm font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all shadow-sm shadow-blue-200 whitespace-nowrap"
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default AIChat;